import React, { createContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import analytics from '../services/analytics';
import type {
  GAEvent,
  GAEcommerceItem,
  GAEcommerceEvent,
  GAPageView,
  GAListMetadata,
  GACheckoutMetadata,
} from '../services/analytics';

export interface AnalyticsContextType {
  isInitialized: boolean;
  isEnabled: boolean;
  trackPageView: (pageData?: Partial<GAPageView>) => void;
  trackEvent: (event: GAEvent) => void;
  trackPurchase: (purchaseData: GAEcommerceEvent) => void;
  trackAddToCart: (item: GAEcommerceItem) => void;
  trackRemoveFromCart: (item: GAEcommerceItem) => void;
  trackViewItem: (item: GAEcommerceItem, list?: GAListMetadata) => void;
  trackViewItemList: (items: GAEcommerceItem[], list?: GAListMetadata) => void;
  trackSelectItem: (item: GAEcommerceItem, list?: GAListMetadata) => void;
  trackViewCart: (items: GAEcommerceItem[], meta?: GACheckoutMetadata) => void;
  trackBeginCheckout: (items: GAEcommerceItem[], meta?: GACheckoutMetadata) => void;
  trackAddPaymentInfo: (items: GAEcommerceItem[], meta?: GACheckoutMetadata) => void;
  trackAddShippingInfo: (
    items: GAEcommerceItem[],
    meta?: { value?: number; currency?: string; shipping_tier?: string },
  ) => void;
  trackRefund: (refund: {
    transaction_id: string;
    value?: number;
    currency?: string;
    items?: GAEcommerceItem[];
  }) => void;
  trackViewPromotion: (promo: {
    promotion_id?: string;
    promotion_name?: string;
    creative_name?: string;
    creative_slot?: string;
    location_id?: string;
  }) => void;
  trackSelectPromotion: (promo: {
    promotion_id?: string;
    promotion_name?: string;
    creative_name?: string;
    creative_slot?: string;
    location_id?: string;
  }) => void;
  trackEngagement: (action: string, category?: string) => void;
  setUserProperties: (properties: Record<string, string | number | boolean>) => void;
  setUserId: (userId: string) => void;
  setEnabled: (enabled: boolean) => void;
  grantConsent: () => void;
  revokeConsent: () => void;
  hasConsent: () => boolean;
  analytics: typeof analytics;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

interface AnalyticsProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isEnabled, setIsEnabled] = useState(true);

  useEffect(() => {
    const initializeAnalytics = async () => {
      try {
        await analytics.initialize();
        setIsInitialized(analytics.isReady());

        // Track initial page view
        if (analytics.isReady()) {
          analytics.trackPageView();
        }
      } catch (error) {
        console.error('Failed to initialize analytics:', error);
      }
    };

    initializeAnalytics();
  }, []);

  const trackPageView = (pageData?: Partial<GAPageView>) => {
    analytics.trackPageView(pageData);
  };

  const trackEvent = (event: GAEvent) => {
    analytics.trackEvent(event);
  };

  const trackPurchase = (purchaseData: GAEcommerceEvent) => {
    analytics.trackPurchase(purchaseData);
  };

  const trackAddToCart = (item: GAEcommerceItem) => {
    analytics.trackAddToCart(item);
  };

  const trackRemoveFromCart = (item: GAEcommerceItem) => {
    analytics.trackRemoveFromCart(item);
  };

  const trackViewItem = (item: GAEcommerceItem, list?: GAListMetadata) => {
    analytics.trackViewItem(item, list);
  };

  const trackViewItemList = (items: GAEcommerceItem[], list?: GAListMetadata) => {
    analytics.trackViewItemList(items, list);
  };

  const trackSelectItem = (item: GAEcommerceItem, list?: GAListMetadata) => {
    analytics.trackSelectItem(item, list);
  };

  const trackViewCart = (items: GAEcommerceItem[], meta?: GACheckoutMetadata) => {
    analytics.trackViewCart(items, meta);
  };

  const trackBeginCheckout = (items: GAEcommerceItem[], meta?: GACheckoutMetadata) => {
    analytics.trackBeginCheckout(items, meta);
  };

  const trackAddPaymentInfo = (items: GAEcommerceItem[], meta?: GACheckoutMetadata) => {
    analytics.trackAddPaymentInfo(items, meta);
  };

  const trackAddShippingInfo = (
    items: GAEcommerceItem[],
    meta?: { value?: number; currency?: string; shipping_tier?: string },
  ) => {
    analytics.trackAddShippingInfo(items, meta);
  };

  const trackRefund = (refund: {
    transaction_id: string;
    value?: number;
    currency?: string;
    items?: GAEcommerceItem[];
  }) => {
    analytics.trackRefund(refund);
  };

  const trackViewPromotion = (promo: {
    promotion_id?: string;
    promotion_name?: string;
    creative_name?: string;
    creative_slot?: string;
    location_id?: string;
  }) => {
    analytics.trackViewPromotion(promo);
  };

  const trackSelectPromotion = (promo: {
    promotion_id?: string;
    promotion_name?: string;
    creative_name?: string;
    creative_slot?: string;
    location_id?: string;
  }) => {
    analytics.trackSelectPromotion(promo);
  };

  const trackEngagement = (action: string, category: string = 'engagement') => {
    analytics.trackEngagement(action, category);
  };

  const setUserProperties = (properties: Record<string, string | number | boolean>) => {
    analytics.setUserProperties(properties);
  };

  const setUserId = (userId: string) => {
    analytics.setUserId(userId);
  };

  const handleSetEnabled = (enabled: boolean) => {
    setIsEnabled(enabled);
    analytics.setEnabled(enabled);
  };

  const grantConsent = () => {
    analytics.grantConsent();
  };

  const revokeConsent = () => {
    analytics.revokeConsent();
  };

  const hasConsent = () => {
    return analytics.hasConsent();
  };

  const contextValue: AnalyticsContextType = {
    isInitialized,
    isEnabled,
    trackPageView,
    trackEvent,
    trackPurchase,
    trackAddToCart,
    trackRemoveFromCart,
    trackViewItem,
    trackViewItemList,
    trackSelectItem,
    trackViewCart,
    trackBeginCheckout,
    trackAddPaymentInfo,
    trackAddShippingInfo,
    trackRefund,
    trackViewPromotion,
    trackSelectPromotion,
    trackEngagement,
    setUserProperties,
    setUserId,
    setEnabled: handleSetEnabled,
    grantConsent,
    revokeConsent,
    hasConsent,
    analytics,
  };

  return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
};

// useAnalytics hook moved to separate file for react-refresh compliance

export default AnalyticsContext;
